import React, { useState, useEffect } from 'react';
import styles from './jiraSettings.module.css';
import { Form, Row, Col, Switch, message, Select, Button } from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import { get } from '../../utils/fetch';
import { ConnectPrompt } from './ConnectPrompt';
import { useIntegrationConnection } from './useIntegrationConnection';

type OptionType = { value: string; label: string };

interface Props {
  projectId: string;
  organizationId: string;
  integration:
    | {
        enabled: boolean;
        id: string;
        settings: {
          team?: OptionType;
          project?: OptionType;
          state?: OptionType;
          new_tasks?: boolean;
          sync_task_updates?: boolean;
          new_comments?: boolean;
          sync_linear_comments?: boolean;
          sync_linear_status?: boolean;
          sync_linear_assignee?: boolean;
          task_status?: OptionType;
          status_mapping?: Record<string, string>;
          user_mapping?: Record<string, string>;
        };
      }
    | undefined;
  onDisconnect: () => void;
  bugherdUsers?: any[];
}

type Option = OptionType | undefined;

const LinearSettings: React.FC<Props> = ({
  projectId,
  organizationId,
  integration,
  onDisconnect,
  bugherdUsers = []
}) => {
  const [form] = Form.useForm();

  const {
    projectIntegrationId,
    isEnabled,
    getConnectedState,
    saveSettings,
    disconnect: handleDisconnect
  } = useIntegrationConnection({
    organizationId,
    projectId,
    integration,
    integrationCode: 'Linear',
    onDisconnect
  });

  const setting = <T,>(key: string, defaultValue?: T) =>
    integration?.settings?.[key] ?? defaultValue;

  const [selectedTeam, setSelectedTeam] = useState<Option>(setting('team'));
  const [selectedProject, setSelectedProject] = useState<Option>(
    setting('project')
  );
  const [selectedState, setSelectedState] = useState<Option>(setting('state'));
  const [selectedStatus, setSelectedStatus] = useState<Option>(
    setting('task_status')
  );
  const [autoPost, setAutoPost] = useState(setting('new_tasks', false));
  const [syncTaskUpdates, setSyncTaskUpdates] = useState(
    setting('sync_task_updates', true)
  );
  const [newComments, setNewComments] = useState(
    setting('new_comments', false)
  );
  const [syncLinearComments, setSyncLinearComments] = useState(
    setting('sync_linear_comments', false)
  );
  const [syncLinearStatus, setSyncLinearStatus] = useState(
    setting('sync_linear_status', false)
  );
  const [syncLinearAssignee, setSyncLinearAssignee] = useState(
    setting('sync_linear_assignee', false)
  );
  const [statusMappings, setStatusMappings] = useState<{
    [key: string]: string;
  }>({});
  const [teamOptions, setTeamOptions] = useState<OptionType[]>([]);
  const [projectOptions, setProjectOptions] = useState<OptionType[]>([]);
  const [stateOptions, setStateOptions] = useState<OptionType[]>([]);
  const [bugherdColumnOptions, setBugherdColumnOptions] = useState<
    OptionType[]
  >([]);
  const [isTeamsLoading, setIsTeamsLoading] = useState(false);
  const [isProjectsLoading, setIsProjectsLoading] = useState(false);
  const [isStatesLoading, setIsStatesLoading] = useState(false);

  useEffect(() => {
    if (integration?.settings?.status_mapping) {
      const inverted = Object.entries(
        integration.settings.status_mapping
      ).reduce((acc, [linearId, bhId]) => {
        acc[bhId] = linearId;
        return acc;
      }, {} as Record<string, string>);
      setStatusMappings(inverted);
    }
  }, [integration]);

  const getTeams = async () => {
    if (!integration?.enabled) return;
    setIsTeamsLoading(true);
    try {
      const results = await get(
        `/organizations/${organizationId}/integrations/remote_projects?id=${integration.id}&site_name=teams`
      );
      setTeamOptions(results);
    } catch {
      message.error('Failed to fetch Linear teams.');
    } finally {
      setIsTeamsLoading(false);
    }
  };

  const getProjects = async (teamId: string) => {
    if (!teamId) return;
    setIsProjectsLoading(true);
    try {
      const results = await get(
        `/organizations/${organizationId}/integrations/remote_projects?id=${
          integration!.id
        }&site_name=projects:${teamId}`
      );
      setProjectOptions(results);
    } catch {
      message.error('Failed to fetch Linear projects.');
    } finally {
      setIsProjectsLoading(false);
    }
  };

  const getStates = async (teamId: string) => {
    if (!teamId) return;
    setIsStatesLoading(true);
    try {
      const results = await get(
        `/organizations/${organizationId}/integrations/remote_projects?id=${
          integration!.id
        }&site_name=states:${teamId}`
      );
      setStateOptions(results);
    } catch {
      message.error('Failed to fetch Linear workflow states.');
    } finally {
      setIsStatesLoading(false);
    }
  };

  const getBugherdColumns = async () => {
    try {
      const results = await get(`/projects/full?id=${projectId}`);
      const columns: OptionType[] = results.columns.map(
        ({ id, name }: { id: string; name: string }) => ({
          value: String(id),
          label: name
        })
      );
      setBugherdColumnOptions(columns);
    } catch {
      message.error('Failed to fetch BugHerd columns.');
    }
  };

  const handleTeamChange = (
    _value: string | undefined,
    option: OptionType | OptionType[] | undefined
  ) => {
    const next = Array.isArray(option) ? option[0] : option;
    setSelectedTeam(next);
    setSelectedProject(undefined);
    setSelectedState(undefined);
    setStateOptions([]);
    setProjectOptions([]);
    form.setFieldsValue({ project: undefined, state: undefined });
    if (next?.value) {
      getProjects(next.value);
      getStates(next.value);
    }
  };

  const handleProjectChange = (
    _value: string | undefined,
    option: OptionType | OptionType[] | undefined
  ) => {
    setSelectedProject(Array.isArray(option) ? option[0] : option);
  };

  const handleStateChange = (
    _value: string | undefined,
    option: OptionType | OptionType[] | undefined
  ) => {
    setSelectedState(Array.isArray(option) ? option[0] : option);
  };

  const handleTaskStatusChange = (
    _value: string | undefined,
    option: OptionType | OptionType[] | undefined
  ) => {
    setSelectedStatus(Array.isArray(option) ? option[0] : option);
  };

  const handleStatusMappingChange = (
    bugherdColumnId: string,
    linearStateId?: string
  ) => {
    setStatusMappings({
      ...statusMappings,
      [bugherdColumnId]: linearStateId || ''
    });
  };

  const linearStateOptionsForColumn = (bugherdColumnId: string) => {
    const alreadyMapped = Object.entries(statusMappings)
      .filter(([bhId, linearId]) => bhId !== bugherdColumnId && linearId)
      .map(([_, linearId]) => linearId);
    return stateOptions.filter(opt => !alreadyMapped.includes(opt.value));
  };

  const handleSubmit = () => {
    const validBhColumnIds = bugherdColumnOptions.map(opt => opt.value);
    const hasLoadedOptions = validBhColumnIds.length > 0;

    // Invert back to backend shape: { linearStateId -> bugherdColumnId }
    const cleanedStatusMapping = Object.entries(statusMappings).reduce(
      (acc, [bhId, linearId]) => {
        if (!linearId || String(linearId) === '0') return acc;
        if (hasLoadedOptions && !validBhColumnIds.includes(bhId)) return acc;
        acc[linearId] = bhId;
        return acc;
      },
      {} as Record<string, string>
    );

    return saveSettings(
      {
        team: selectedTeam,
        project: selectedProject,
        state: selectedState,
        new_tasks: autoPost,
        sync_task_updates: syncTaskUpdates,
        new_comments: newComments,
        sync_linear_comments: syncLinearComments,
        sync_linear_status: syncLinearStatus,
        sync_linear_assignee: syncLinearAssignee,
        task_status: selectedStatus,
        status_mapping: cleanedStatusMapping
      },
      { form }
    );
  };

  useEffect(() => {
    if (integration?.enabled) {
      getConnectedState();
      getTeams();
      getBugherdColumns();
    }
  }, [integration, projectId, organizationId]);

  useEffect(() => {
    if (selectedTeam?.value) {
      getProjects(selectedTeam.value);
      getStates(selectedTeam.value);
    }
  }, [selectedTeam?.value]);

  const renderStatusMappings = () => (
    <div className={styles.statusMappings}>
      <Row className={styles.statusMappingRow} gutter={16}>
        <Col span={11}>
          <strong>BugHerd Status</strong>
        </Col>
        <Col span={2} />
        <Col span={11}>
          <strong>Linear State</strong>
        </Col>
      </Row>
      {bugherdColumnOptions.map(bhColumn => (
        <Row
          key={bhColumn.value}
          className={styles.statusMappingRow}
          gutter={16}
          align="middle"
        >
          <Col span={11}>
            <span className={styles.jiraStatusLabel}>{bhColumn.label}</span>
          </Col>
          <Col span={2} style={{ textAlign: 'center' }}>
            <span>↔</span>
          </Col>
          <Col span={11}>
            <Select
              className={styles.bugherdColumnSelect}
              showSearch
              placeholder="Select Linear state"
              optionFilterProp="label"
              value={statusMappings[bhColumn.value] || undefined}
              onChange={value =>
                handleStatusMappingChange(bhColumn.value, value)
              }
              options={linearStateOptionsForColumn(bhColumn.value)}
              allowClear
            />
          </Col>
        </Row>
      ))}
    </div>
  );

  const renderSettings = () => (
    <Form
      form={form}
      layout="horizontal"
      onFinish={handleSubmit}
      autoComplete="off"
      requiredMark={false}
    >
      <div className={styles.jiraRequired}>
        <div className={styles.requiredBubble}>REQUIRED</div>
        <h3>Connect your Linear team</h3>

        <Row className={styles.jiraEditRow}>
          <Col span={24}>
            <Form.Item
              name="team"
              label={
                <span className={styles.requiredLabel}>
                  <span>*</span>Linear Team
                </span>
              }
              className={styles.jiraProject}
              rules={[
                { required: true, message: 'Please select a Linear team.' }
              ]}
              initialValue={selectedTeam?.value}
            >
              <Select
                className={styles.jiraSelect}
                showSearch
                placeholder="Select a team"
                optionFilterProp="label"
                options={teamOptions}
                onChange={handleTeamChange}
                loading={isTeamsLoading}
                allowClear
              />
            </Form.Item>
          </Col>
        </Row>

        {selectedTeam && (
          <>
            <Row className={styles.jiraEditRow}>
              <Col span={24}>
                <Form.Item
                  name="project"
                  label="Linear Project (optional)"
                  className={styles.jiraProject}
                  initialValue={selectedProject?.value}
                >
                  <Select
                    className={styles.jiraSelect}
                    showSearch
                    placeholder="Select a project (optional)"
                    optionFilterProp="label"
                    options={projectOptions}
                    onChange={handleProjectChange}
                    loading={isProjectsLoading}
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row className={styles.jiraEditRow}>
              <Col span={24}>
                <Form.Item
                  name="state"
                  label="Default workflow state"
                  className={styles.jiraProject}
                  initialValue={selectedState?.value}
                >
                  <Select
                    className={styles.jiraSelect}
                    showSearch
                    placeholder="Select a default state"
                    optionFilterProp="label"
                    options={stateOptions}
                    onChange={handleStateChange}
                    loading={isStatesLoading}
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}
      </div>

      <div className={styles.jiraWorkflows}>
        <h3>Choose your desired workflow</h3>

        <div className={styles.linearWorkflowRows}>
          <Row className={styles.jiraEditRow}>
            <Col span={24}>
              <Form.Item
                label="Automatically post new tasks to Linear"
                className={styles.autoPost}
              >
                <Switch checked={autoPost} onChange={setAutoPost} />
              </Form.Item>
            </Col>
          </Row>

          {autoPost && (
            <Row className={styles.jiraEditRow}>
              <Col span={24}>
                <Form.Item
                  name="statusColumn"
                  label="Create task when status is equal to"
                  className={styles.selectStatus}
                  initialValue={selectedStatus?.value}
                  tooltip="Optional. Select a BugHerd column to trigger task creation."
                >
                  <Select
                    placeholder="Select a status"
                    options={bugherdColumnOptions}
                    onChange={handleTaskStatusChange}
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>
          )}

          <Row className={styles.jiraEditRow}>
            <Col span={24}>
              <Form.Item label="Automatically sync task detail updates to Linear">
                <Switch
                  checked={syncTaskUpdates}
                  onChange={setSyncTaskUpdates}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row className={styles.jiraEditRow}>
            <Col span={24}>
              <Form.Item
                className={styles.newComments}
                label="Automatically send new BugHerd comments to Linear"
              >
                <Switch checked={newComments} onChange={setNewComments} />
              </Form.Item>
            </Col>
          </Row>

          <Row className={styles.jiraEditRow}>
            <Col span={24}>
              <Form.Item
                className={styles.syncComments}
                label="Sync comments from Linear to BugHerd"
              >
                <Switch
                  checked={syncLinearComments}
                  onChange={setSyncLinearComments}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row className={styles.jiraEditRow}>
            <Col span={24}>
              <Form.Item label="Sync status updates between Linear and BugHerd">
                <Switch
                  checked={syncLinearStatus}
                  onChange={setSyncLinearStatus}
                />
              </Form.Item>
            </Col>
          </Row>

          {syncLinearStatus && stateOptions.length > 0 && (
            <div className={styles.jiraEditRow}>{renderStatusMappings()}</div>
          )}

          <Row className={styles.jiraEditRow}>
            <Col span={24}>
              <Form.Item label="Sync assignee updates from Linear to BugHerd">
                <Switch
                  checked={syncLinearAssignee}
                  onChange={setSyncLinearAssignee}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row className={styles.jiraEditRow}>
            <Col span={24}>
              <Form.Item
                className={styles.syncUsers}
                label="Sync users between Linear and BugHerd"
              >
                <a
                  href={`/settings/organizations/${organizationId}/team?manage_linear_users=true&integration_id=${integration?.id}&project_integration_id=${projectIntegrationId}&team_id=${selectedTeam?.value}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.manageLink}
                >
                  Manage <ExportOutlined style={{ marginLeft: '5px' }} />
                </a>
              </Form.Item>
            </Col>
          </Row>
        </div>
      </div>

      <Row
        className={styles.jiraEditRow}
        justify="space-between"
        align="middle"
      >
        <Col>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              shape="round"
              htmlType="submit"
              className={styles.save}
            >
              Save
            </Button>
          </Form.Item>
        </Col>
        <Col>
          <Button
            type="text"
            shape="round"
            onClick={() => handleDisconnect({ deleteFallback: true })}
            className={styles.disconnectButton}
            danger
          >
            Disconnect
          </Button>
        </Col>
      </Row>
    </Form>
  );

  return (
    <div className={styles.container}>
      {isEnabled ? (
        renderSettings()
      ) : (
        <ConnectPrompt
          integrationName="linear"
          projectId={projectId}
          organizationId={organizationId}
          type="Linear"
          display="Linear"
          provider="linear"
        />
      )}
    </div>
  );
};

export default LinearSettings;
